import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import classNames from "classnames";
import SectionHeader from "@/components/Home/v2/shared/SectionHeader";

export type SplitOutcomeCardAccent = "blue" | "green" | "yellow" | "pink";

export type SplitOutcomeCard = {
  title: string;
  description?: string;
  tags?: string[];
  accent: SplitOutcomeCardAccent;
  image?: string;
};

export type SplitOutcomeCardsSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  cards: SplitOutcomeCard[];
};

const accentImageBg: Record<SplitOutcomeCardAccent, string> = {
  blue: "bg-gradient-to-br from-[#E8F1FF] to-[#9DC3FF]",
  green: "bg-gradient-to-br from-[#E7FAE3] to-[#9DE88A]",
  yellow: "bg-gradient-to-br from-[#FFF6D9] to-[#FFD06A]",
  pink: "bg-gradient-to-br from-[#FFE6F2] to-[#FFAAD0]",
};

const Card = ({ card }: { card: SplitOutcomeCard }) => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-sm">
    {/* Illustration area */}
    <div
      className={classNames(
        "relative w-full overflow-hidden",
        accentImageBg[card.accent]
      )}
      style={{ aspectRatio: "4/3" }}
    >
      {card.image && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={card.image}
          alt={card.title}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      )}
    </div>

    {/* Text area */}
    <div className="flex flex-col gap-3 p-5 md:p-6">
      <h3 className="font-inter text-[18px] font-medium leading-snug text-neutralBlack md:text-[20px]">
        {card.title}
      </h3>

      {card.description && (
        <p className="font-inter text-[14px] leading-relaxed text-neutralBlack/65 md:text-[15px]">
          {card.description}
        </p>
      )}

      {!card.description && card.tags && (
        <div className="flex flex-wrap gap-2">
          {card.tags.map((t) => (
            <span
              key={t}
              className="rounded-full bg-[#F3F4F6] px-3 py-1.5 font-inter text-[13px] font-medium text-neutralBlack"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SplitOutcomeCardsSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  cards,
}: SplitOutcomeCardsSectionData) => (
  <section className="relative w-full bg-white">
    <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
      <SectionHeader
        eyebrow={eyebrow}
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        subtitle={subtitle}
        ctas={ctas}
        variant="light-teal"
        align="center"
      />
      <div
        className={classNames(
          "mx-auto mt-12 grid max-w-6xl gap-5 md:mt-16 md:gap-6",
          cards.length === 4
            ? "md:grid-cols-2 lg:grid-cols-4"
            : cards.length === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2"
        )}
      >
        {cards.map((c) => (
          <Card key={c.title} card={c} />
        ))}
      </div>
    </div>
  </section>
);

export default SplitOutcomeCardsSection;
